import {
  View,
  Text,
  ScrollView,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  TextInput,
  KeyboardAvoidingView,
  Platform,
} from "react-native";
import MaterialIcons from "@expo/vector-icons/MaterialIcons";
import { useForm, Controller } from "react-hook-form";
import { Feather } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import Animated, { FadeInDown } from "react-native-reanimated";
import {
  widthPercentageToDP as wp,
  heightPercentageToDP as hp,
} from "react-native-responsive-screen";
import { useEffect, useState } from "react";
import { router } from "expo-router";
import { Toast } from "react-native-toast-notifications";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useDispatch } from "react-redux";
import { authActions } from "../../../../store/slices/authSlice";
import customAxios from "../../../../utils/customAxios";
import i18n, { availableLocales } from "../../../../locales/i18n";
import WebSignupScreen from "../../../../components/web/WebSignupScreen";

export default function SignUpScreen() {
  // Render web version on web platform
  if (Platform.OS === "web") {
    return <WebSignupScreen />;
  }

  const locale = i18n.locale;
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [showlanguages, setShowLanguages] = useState(false);

  const EmailRegx = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      username: "",
      gmail: "",
      storename: "",
      Password: "",
      phone: "",
    },
  });
  interface authData {
    username: string;
    gmail: string;
    storename: string;
    Password: string;
    phone: string;
  }

  const [buttonSpinner, setButtonSpinner] = useState(false);

  const handleSignUp = async (data: authData) => {
    setButtonSpinner(true);
    try {
      // Register the user
      const response = await customAxios.post(`/users/register`, {
        username: data.username,
        email: data.gmail,
        password: data.Password,
        storename: data.storename,
        phone: data.phone,
      });

      Toast.show(response.data.message || i18n.t("registrationSuccessful"), {
        type: "success",
      });

      // Automatically log the user in after successful registration
      try {
        const loginResponse = await customAxios.post(`/users/login`, {
          email: data.gmail,
          password: data.Password,
        });

        // Store token and user data
        const token = loginResponse.data.token;
        const userData = loginResponse.data.user;

        await AsyncStorage.setItem("token", token);
        await AsyncStorage.setItem("user", JSON.stringify(userData));

        dispatch(authActions.setUser(userData));

        // Show reminder about email verification
        Toast.show(i18n.t("verifyEmailReminder"), {
          type: "info",
          duration: 6000,
        });

        setButtonSpinner(false);

        // Navigate to main app
        router.push("/(drawer)/start");
      } catch (loginError: any) {
        setButtonSpinner(false);

        // If auto-login fails, redirect to login page
        Toast.show(i18n.t("pleaseLoginManually"), {
          type: "warning",
        });
        router.push("/(routes)/(auth)/login");
      }
    } catch (error: any) {
      setButtonSpinner(false);

      // Handle different types of errors
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        i18n.t("unexpectedError");

      Toast.show(errorMessage, {
        type: "danger",
      });
    }
  };

  const ChangeLang = () => {
    setShowLanguages(!showlanguages);
  };

  return (
    <LinearGradient
      colors={["#004E6D", "#0596D0"]}
      locations={[0, 1]}
      style={{ flex: 1 }}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={{ flex: 1 }}
        keyboardVerticalOffset={Platform.OS === "ios" ? 0 : 20}
      >
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ flexGrow: 1 }}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Top section with logo and curved bottom */}
          <View
            style={{
              alignItems: "center",
              paddingTop: hp("8%"),
              paddingBottom: hp("6%"),
            }}
          >
            <Image
              style={{
                width: wp("20%"),
                height: wp("20%"),
                tintColor: "white",
                marginBottom: hp("3%"),
              }}
              source={require("../../../../assets/logo-no-bg.png")}
              resizeMode="contain"
            />

            {/* White curved section */}
            <View
              style={{
                backgroundColor: "white",
                width: wp("100%"),
                minHeight: hp("80%"),
                borderTopLeftRadius: 60,
                borderTopRightRadius: 60,
                paddingHorizontal: wp("8%"),
                paddingTop: hp("4%"),
                marginTop: hp("2%"),
              }}
            >
              {/* Title */}
              <Animated.View entering={FadeInDown.duration(400).delay(100)}>
                <Text
                  style={{
                    fontSize: 24,
                    fontWeight: "bold",
                    textAlign: "center",
                    color: "#004E6D",
                    marginBottom: hp("4%"),
                    fontFamily: "kawkabBold",
                  }}
                >
                  {i18n.t("signupTitle")}
                </Text>
              </Animated.View>

              {/* Username Field */}
              <Animated.View
                entering={FadeInDown.duration(400).delay(200)}
                style={{ marginBottom: hp("2%") }}
              >
                <Text
                  style={{
                    fontSize: 16,
                    color: "#666",
                    marginBottom: hp("1%"),
                    textAlign: locale === "ar" ? "right" : "left",
                  }}
                >
                  {i18n.t("username")}
                </Text>
                <View
                  style={{
                    borderWidth: 1,
                    borderColor: "#E0E0E0",
                    borderRadius: 25,
                    backgroundColor: "#F9F9F9",
                    flexDirection: "row",
                    alignItems: "center",
                    paddingHorizontal: wp("4%"),
                    height: 50,
                  }}
                >
                  <Feather
                    name="user"
                    size={20}
                    color="#999"
                    style={{ marginRight: 10 }}
                  />
                  <Controller
                    control={control}
                    name="username"
                    rules={{
                      required: i18n.t("usernameRequired"),
                    }}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <TextInput
                        style={{
                          flex: 1,
                          textAlign: locale === "ar" ? "right" : "left",
                          fontSize: 16,
                          color: "#000",
                        }}
                        placeholder={i18n.t("username")}
                        placeholderTextColor="#999"
                        autoCapitalize="none"
                        value={value}
                        onChangeText={onChange}
                        onBlur={onBlur}
                      />
                    )}
                  />
                </View>
                {errors.username && (
                  <Text
                    style={{
                      color: "red",
                      textAlign: locale === "ar" ? "right" : "left",
                      marginTop: 5,
                    }}
                  >
                    {errors.username.message}
                  </Text>
                )}
              </Animated.View>

              {/* Email Field */}
              <Animated.View
                entering={FadeInDown.duration(400).delay(300)}
                style={{ marginBottom: hp("2%") }}
              >
                <Text
                  style={{
                    fontSize: 16,
                    color: "#666",
                    marginBottom: hp("1%"),
                    textAlign: locale === "ar" ? "right" : "left",
                  }}
                >
                  {i18n.t("email")}
                </Text>
                <View
                  style={{
                    borderWidth: 1,
                    borderColor: "#E0E0E0",
                    borderRadius: 25,
                    backgroundColor: "#F9F9F9",
                    flexDirection: "row",
                    alignItems: "center",
                    paddingHorizontal: wp("4%"),
                    height: 50,
                  }}
                >
                  <Feather
                    name="mail"
                    size={20}
                    color="#999"
                    style={{ marginRight: 10 }}
                  />
                  <Controller
                    control={control}
                    name="gmail"
                    rules={{
                      required: i18n.t("mailRequired"),
                      pattern: {
                        value: EmailRegx,
                        message: i18n.t("invalidEmail"),
                      },
                    }}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <TextInput
                        style={{
                          flex: 1,
                          textAlign: locale === "ar" ? "right" : "left",
                          fontSize: 16,
                          color: "#000",
                        }}
                        placeholder={i18n.t("email")}
                        placeholderTextColor="#999"
                        autoCapitalize="none"
                        value={value}
                        onChangeText={onChange}
                        onBlur={onBlur}
                        keyboardType="email-address"
                      />
                    )}
                  />
                </View>
                {errors.gmail && (
                  <Text
                    style={{
                      color: "red",
                      textAlign: locale === "ar" ? "right" : "left",
                      marginTop: 5,
                    }}
                  >
                    {errors.gmail.message}
                  </Text>
                )}
              </Animated.View>

              {/* Password Field */}
              <Animated.View
                entering={FadeInDown.duration(400).delay(400)}
                style={{ marginBottom: hp("2%") }}
              >
                <Text
                  style={{
                    fontSize: 16,
                    color: "#666",
                    marginBottom: hp("1%"),
                    textAlign: locale === "ar" ? "right" : "left",
                  }}
                >
                  {i18n.t("password")}
                </Text>
                <View
                  style={{
                    borderWidth: 1,
                    borderColor: "#E0E0E0",
                    borderRadius: 25,
                    backgroundColor: "#F9F9F9",
                    flexDirection: "row",
                    alignItems: "center",
                    paddingHorizontal: wp("4%"),
                    height: 50,
                  }}
                >
                  <Feather
                    name="lock"
                    size={20}
                    color="#999"
                    style={{ marginRight: 10 }}
                  />
                  <Controller
                    control={control}
                    name="Password"
                    rules={{
                      required: i18n.t("passwordRequired"),
                      minLength: {
                        value: 3,
                        message: i18n.t("passwordMinLength"),
                      },
                    }}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <TextInput
                        style={{
                          flex: 1,
                          textAlign: locale === "ar" ? "right" : "left",
                          fontSize: 16,
                          color: "#000",
                        }}
                        placeholder="••••••••"
                        placeholderTextColor="#999"
                        autoCapitalize="none"
                        value={value}
                        onChangeText={onChange}
                        onBlur={onBlur}
                        secureTextEntry={showPassword}
                      />
                    )}
                  />
                  {!showPassword ? (
                    <Feather
                      onPress={() => {
                        setShowPassword(true);
                      }}
                      name="eye-off"
                      size={20}
                      color="#999"
                      style={{ marginLeft: 10 }}
                    />
                  ) : (
                    <Feather
                      onPress={() => {
                        setShowPassword(false);
                      }}
                      name="eye"
                      size={20}
                      color="#999"
                      style={{ marginLeft: 10 }}
                    />
                  )}
                </View>
                {errors.Password && (
                  <Text
                    style={{
                      color: "red",
                      textAlign: locale === "ar" ? "right" : "left",
                      marginTop: 5,
                    }}
                  >
                    {errors.Password.message}
                  </Text>
                )}
              </Animated.View>

              {/* Store Name Field */}
              <Animated.View
                entering={FadeInDown.duration(400).delay(500)}
                style={{ marginBottom: hp("3%") }}
              >
                <Text
                  style={{
                    fontSize: 16,
                    color: "#666",
                    marginBottom: hp("1%"),
                    textAlign: locale === "ar" ? "right" : "left",
                  }}
                >
                  {i18n.t("storeName")}
                </Text>
                <View
                  style={{
                    borderWidth: 1,
                    borderColor: "#E0E0E0",
                    borderRadius: 25,
                    backgroundColor: "#F9F9F9",
                    flexDirection: "row",
                    alignItems: "center",
                    paddingHorizontal: wp("4%"),
                    height: 50,
                  }}
                >
                  <Feather
                    name="shopping-bag"
                    size={20}
                    color="#999"
                    style={{ marginRight: 10 }}
                  />
                  <Controller
                    control={control}
                    name="storename"
                    rules={{
                      required: i18n.t("storeNameRequired"),
                    }}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <TextInput
                        style={{
                          flex: 1,
                          textAlign: locale === "ar" ? "right" : "left",
                          fontSize: 16,
                          color: "#000",
                        }}
                        placeholder={i18n.t("storeName")}
                        placeholderTextColor="#999"
                        autoCapitalize="none"
                        value={value}
                        onChangeText={onChange}
                        onBlur={onBlur}
                      />
                    )}
                  />
                </View>
                {errors.storename && (
                  <Text
                    style={{
                      color: "red",
                      textAlign: locale === "ar" ? "right" : "left",
                      marginTop: 5,
                    }}
                  >
                    {errors.storename.message}
                  </Text>
                )}
              </Animated.View>
              {/* mobile number Field */}
              <Animated.View
                entering={FadeInDown.duration(400).delay(600)}
                style={{ marginBottom: hp("3%") }}
              >
                <Text
                  style={{
                    fontSize: 16,
                    color: "#666",
                    marginBottom: hp("1%"),
                    textAlign: locale === "ar" ? "right" : "left",
                  }}
                >
                  {i18n.t("phoneNumber")}
                </Text>
                <View
                  style={{
                    borderWidth: 1,
                    borderColor: "#E0E0E0",
                    borderRadius: 25,
                    backgroundColor: "#F9F9F9",
                    flexDirection: "row",
                    alignItems: "center",
                    paddingHorizontal: wp("4%"),
                    height: 50,
                  }}
                >
                  <Feather
                    name="phone"
                    size={20}
                    color="#999"
                    style={{ marginRight: 10 }}
                  />
                  <Controller
                    control={control}
                    name="phone"
                    rules={{
                      required: i18n.t("phoneRequired"),
                      pattern: {
                        value: /^\d{10}$/,
                        message: i18n.t("invalidPhoneNumber"),
                      },
                    }}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <TextInput
                        style={{
                          flex: 1,
                          textAlign: locale === "ar" ? "right" : "left",
                          fontSize: 16,
                          color: "#000",
                        }}
                        placeholder="0779966025"
                        placeholderTextColor="#999"
                        autoCapitalize="none"
                        keyboardType="phone-pad"
                        value={value}
                        onChangeText={onChange}
                        onBlur={onBlur}
                      />
                    )}
                  />
                </View>
                {errors.phone && (
                  <Text
                    style={{
                      color: "red",
                      textAlign: locale === "ar" ? "right" : "left",
                      marginTop: 5,
                    }}
                  >
                    {errors.phone.message}
                  </Text>
                )}
              </Animated.View>

              {/* Signup Button */}
              <Animated.View entering={FadeInDown.duration(400).delay(700)}>
                <TouchableOpacity
                  style={{
                    marginBottom: hp("3%"),
                    borderRadius: 25,
                    overflow: "hidden",
                  }}
                  onPress={handleSubmit(handleSignUp)}
                >
                  <LinearGradient
                    colors={["#004E6D", "#0596D0"]}
                    locations={[0, 1]}
                    style={{
                      paddingVertical: hp("2%"),
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {buttonSpinner ? (
                      <ActivityIndicator size="small" color="white" />
                    ) : (
                      <Text
                        style={{
                          color: "white",
                          textAlign: "center",
                          fontSize: 18,
                          fontWeight: "bold",
                        }}
                      >
                        {i18n.t("signup")}
                      </Text>
                    )}
                  </LinearGradient>
                </TouchableOpacity>
              </Animated.View>

              {/* Links */}
              <Animated.View
                entering={FadeInDown.duration(400).delay(800)}
                style={{ alignItems: "center", marginBottom: hp("4%") }}
              >
                <TouchableOpacity
                  onPress={() => router.push("/(routes)/(auth)/login")}
                >
                  <Text
                    style={{
                      color: "#004E6D",
                      fontSize: 16,
                      textDecorationLine: "underline",
                      marginBottom: hp("1%"),
                    }}
                  >
                    {i18n.t("alreadyHaveAccount")} {i18n.t("signIn")}
                  </Text>
                </TouchableOpacity>
              </Animated.View>

              {/* Language Selection */}
              <Animated.View
                entering={FadeInDown.duration(400).delay(900)}
                style={{ alignItems: "center", marginBottom: hp("2%") }}
              >
                {!showlanguages && (
                  <TouchableOpacity
                    onPress={ChangeLang}
                    style={{
                      backgroundColor: "#B0B0B0",
                      flexDirection: "row",
                      alignItems: "center",
                      paddingHorizontal: wp("8%"),
                      paddingVertical: hp("1.5%"),
                      borderRadius: 10,
                      gap: 8,
                    }}
                  >
                    <MaterialIcons name="expand-more" size={24} color="black" />
                    <Text
                      style={{
                        color: "black",
                        fontSize: 16,
                        fontWeight: "bold",
                      }}
                    >
                      {i18n.t("language")}
                    </Text>
                    <MaterialIcons name="language" size={24} color="black" />
                  </TouchableOpacity>
                )}

                {showlanguages && (
                  <View style={{ width: wp("70%") }}>
                    {availableLocales.map((item) => (
                      <TouchableOpacity
                        key={item.code}
                        onPress={() => {
                          setShowLanguages(false);
                          i18n.locale = item.code;
                        }}
                        style={{
                          backgroundColor:
                            i18n.locale === item.code ? "#004E6D" : "#E0E0E0",
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "center",
                          paddingHorizontal: wp("6%"),
                          paddingVertical: hp("1.5%"),
                          borderRadius: 10,
                          marginBottom: hp("1%"),
                          elevation: 2,
                          shadowColor: "#000",
                          shadowOffset: {
                            width: 0,
                            height: 1,
                          },
                          shadowOpacity: 0.22,
                          shadowRadius: 2.22,
                        }}
                      >
                        <Text
                          style={{
                            fontWeight: "bold",
                            textAlign: "center",
                            fontSize: 16,
                            color:
                              i18n.locale === item.code ? "white" : "black",
                          }}
                        >
                          {item.name}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
              </Animated.View>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </LinearGradient>
  );
}
